import { FC, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Formik, FormikProps } from "formik"
import { object, string } from "yup"
import { LeadworkflowDetails } from "../../../types"
import ManageLeadworkflowFormContent from "../../../common/ui/config-admin/config-lead-workflow/manage/form-content"
import Tabs from "../../../common/ui/tabs"
import ConfigEditHeader from "../../../common/ui/config-admin/config-edit-header"
import {
  useDeleteLeadworkflowMutation,
  useFetchAllBrandsQuery,
  useFetchDefaultLeadworkflowDetailsQuery,
  useFetchLeadworkflowDetailsQuery,
  useUpdateOrCreateLeadworkflowMutation
} from "../../../redux/queries"
import { ConfigEditPageSkeleton } from "../../../common/ui/skeletons"
import { useAppDispatch } from "../../../common/hooks"
import { useConfigMutationFeedback } from "../use-config-mutation-feedback"

const BACK_TAB = "/config-admin?tab=lead-workflow"
const backUrl = (brandId?: string) =>
  brandId ? `${BACK_TAB}&brand_id=${brandId}` : BACK_TAB

const TABS = [
  { id: "behaviour", label: "Behaviour" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "steps", label: "Steps" }
]

const LeadworkflowSchema = object({
  name: string().required().min(1),
  config_json: object().required(),
  brand_id: string().required().min(1)
})

const LeadWorkflowEditPage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const isCreate = id === "new"
  const [tab, setTab] = useState("behaviour")

  const { data: brands } = useFetchAllBrandsQuery({ limit: 999 })

  const { data: workflowData, isLoading } = useFetchLeadworkflowDetailsQuery(
    { id: id ?? "" },
    { skip: isCreate || !id }
  )

  const { data: defaultData } = useFetchDefaultLeadworkflowDetailsQuery(
    {},
    { skip: !isCreate }
  )

  const [updateOrCreate, saveResult] = useUpdateOrCreateLeadworkflowMutation()
  const [deleteLeadworkflow, deleteResult] = useDeleteLeadworkflowMutation()

  const formRef = useRef<FormikProps<LeadworkflowDetails>>(null)

  useConfigMutationFeedback({
    label: "lead workflow",
    isCreate,
    saveResult,
    deleteResult,
    navigate,
    backUrl: () => backUrl(formRef.current?.values.brand_id),
    dispatch
  })

  const raw = workflowData ?? {
    name: "",
    id: "",
    brand_id: "",
    ...(defaultData || { is_default: "0", config_json: {} })
  }

  const initialValues: LeadworkflowDetails = {
    ...raw,
    config_json: {
      show_txt_msgs_disclaimer: false,
      ...raw.config_json
    }
  }

  const setFormField = (name: string, val: any) => {
    formRef.current?.setFieldValue(name, val)
  }

  const onSubmit = () => {
    if (formRef.current?.values) updateOrCreate(formRef.current.values)
  }

  const onDelete = () => {
    if (workflowData?.id) deleteLeadworkflow(workflowData.id)
  }

  if (!isCreate && isLoading) {
    return <ConfigEditPageSkeleton tabCount={3} />
  }

  const pageTitle = isCreate
    ? "New lead workflow"
    : workflowData?.name ?? "Lead workflow"

  return (
    <div className="min-h-screen bg-gray-50">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={formRef}
        validationSchema={LeadworkflowSchema}
        enableReinitialize
      >
        {({ values, isValid, dirty }) => (
          <>
            <ConfigEditHeader
              backLabel="Lead workflows"
              namePlaceholder="Workflow name"
              name={values.name}
              pageTitle={pageTitle}
              isCreate={isCreate}
              isValid={isValid}
              dirty={dirty}
              isDefault={values.is_default === "1"}
              brands={brands ?? []}
              brandId={values.brand_id}
              onNameChange={val => setFormField("name", val)}
              onDefaultChange={v => setFormField("is_default", v ? "1" : "0")}
              onBrandChange={val => setFormField("brand_id", val)}
              onDelete={onDelete}
              onCancel={() => navigate(backUrl(values.brand_id))}
              onSubmit={onSubmit}
              onBack={() => navigate(backUrl(values.brand_id))}
            />

            <Tabs
              currentTab={tab}
              tabs={TABS}
              onChange={setTab}
              className="px-6 bg-white"
            />

            <div className="bg-white">
              <div className="max-w-2xl py-6 px-6">
                <ManageLeadworkflowFormContent
                  values={values}
                  tab={tab}
                  setFormField={setFormField}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}

export default LeadWorkflowEditPage
